import { OrderMethod } from '@/domain/models/Order/orderMethod';
import { itemForm } from '@/pages/camel-order/coupons/create/service';
import { FormError, validBlank, validMaxStr } from '@/validate';
import { format } from '@/validate/message';

export type ItemFormError = {
  name: FormError;
  couponCode: FormError;
  discount: FormError;
  conditionValue: FormError;
  maxUsagePerUser: FormError;
  availableOrderMethod: FormError;
  validFrom: FormError;
  validTo: FormError;
  status: FormError;
};

export const validateName = (
  name: string,
  error: ItemFormError
): ItemFormError => {
  error.name.isTouched = true;
  error.name.message = '';
  const label = itemForm.name.label;
  const isMax = validMaxStr.check(name, 255);
  const isBlank = validBlank.check(name);
  if (isBlank) error.name.message = validBlank.message(label);
  if (isMax) error.name.message = validMaxStr.message(label, 255);
  error.name.isError = isBlank || isMax;
  return error;
};

export const validateCouponCode = (
  couponCode: string,
  error: ItemFormError
): ItemFormError => {
  error.couponCode.isTouched = true;
  error.couponCode.message = '';
  const label = itemForm.couponCode.label;
  const isMax = validMaxStr.check(couponCode, 255);
  const isBlank = validBlank.check(couponCode);
  const pattern = /^[a-zA-Z0-9]*$/;
  let isValidFormat = false;
  if (!pattern.test(couponCode)) {
    isValidFormat = true;
  }
  if (isBlank) error.couponCode.message = validBlank.message(label);
  if (isMax) error.couponCode.message = validMaxStr.message(label, 255);
  if (isValidFormat) error.couponCode.message = format(label);
  error.couponCode.isError = isBlank || isMax || isValidFormat;
  return error;
};

export const validateDiscount = (
  discount: string,
  error: ItemFormError
): ItemFormError => {
  error.discount.isTouched = true;
  error.discount.message = '';
  const label = itemForm.discount.label;
  const pattern = /^([0-9]*)$/;
  let isValidFormat = false;
  if (!pattern.test(discount)) {
    isValidFormat = true;
  }
  const isBlank = validBlank.check(discount);
  if (isBlank) error.discount.message = validBlank.message(label);
  if (isValidFormat) error.discount.message = format(label);
  error.discount.isError = isBlank || isValidFormat;
  return error;
};

export const validateConditionValue = (
  conditionValue: string,
  error: ItemFormError
): ItemFormError => {
  error.conditionValue.isTouched = true;
  error.conditionValue.message = '';
  const label = itemForm.conditionValue.label;
  const pattern = /^([0-9]*)$/;
  let isValidFormat = false;
  if (!pattern.test(conditionValue)) {
    isValidFormat = true;
  }
  if (isValidFormat) error.conditionValue.message = format(label);
  error.conditionValue.isError = isValidFormat;
  return error;
};

export const validateMaxUsagePerUser = (
  maxUsagePerUser: string,
  error: ItemFormError
): ItemFormError => {
  error.maxUsagePerUser.isTouched = true;
  error.maxUsagePerUser.message = '';
  const label = itemForm.maxUsagePerUser.label;
  const pattern = /^([0-9]*)$/;
  let isValidFormat = false;
  if (!pattern.test(maxUsagePerUser)) {
    isValidFormat = true;
  }
  if (isValidFormat) error.maxUsagePerUser.message = format(label);
  error.maxUsagePerUser.isError = isValidFormat;
  return error;
};

export const validateAvailableOrderMethods = (
  command: {
    selectedOrderMethod: OrderMethod;
    flag: boolean;
  },
  currentAvailableOrderMethods: OrderMethod[],
  error: ItemFormError
): ItemFormError => {
  if (command.flag) {
    // 追加時にエラーが増えることは無いので、エラーをリセット
    error.availableOrderMethod.isTouched = false;
    error.availableOrderMethod.message = '';
    error.availableOrderMethod.isError = false;
  } else {
    const isNoAvialableOrderMethod = currentAvailableOrderMethods.length === 1;
    if (isNoAvialableOrderMethod) {
      error.availableOrderMethod.isTouched = true;
      error.availableOrderMethod.message =
        '対応注文方式は最低一つ選ぶ必要があります';
      error.availableOrderMethod.isError = true;
    }
  }
  return error;
};

export const validateValidDateRange = (
  validFrom: Date | null,
  validTo: Date | null,
  error: ItemFormError
): ItemFormError => {
  error.validFrom.isTouched = true;
  error.validFrom.isError = false;
  error.validFrom.message = '';

  if (validFrom && validTo) {
    if (validFrom >= validTo) {
      error.validFrom.message = '利用開始日は利用終了日より前にしてください';
      error.validFrom.isError = true;
    }
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (validFrom && validFrom < today) {
    error.validFrom.message = '利用開始日は本日以降にしてください';
    error.validFrom.isError = true;
  }
  if (validTo && validTo < today) {
    error.validFrom.message = '利用終了日は本日以降にしてください';
    error.validFrom.isError = true;
  }
  return error;
};

export const validateStatus = (
  status: string,
  error: ItemFormError
): ItemFormError => {
  error.status.isTouched = true;
  const label = itemForm.status.label;
  const isMax = validMaxStr.check(status, 255);
  const isBlank = validBlank.check(status);
  if (isBlank) error.status.message = validBlank.message(label);
  if (isMax) error.status.message = validMaxStr.message(label, 255);
  error.status.isError = isBlank || isMax;
  return error;
};
